import React, { useRef, useState } from "react";
import { FlexColumn, FlexRow } from "@/ui/FlexContainer/FlexContainer";
import Typography from "@/ui/Typography/Typography";
import Button from "@/ui/Button/Button";
import { SaveIcon, ShareIcon, CheckMark } from "@/assets/icons";
import { QRCodeSVG } from "qrcode.react";
import { useUserInfoStore } from "@/store/userInfoStore";
import * as htmlToImage from "html-to-image";
import cls from "./styles.module.scss";
import { CloseBtn } from "../CloseBtn/CloseBtn";

interface QrSectionProps {
  inConditions: boolean;
  variant: "delivery" | "taxi" | "eats";
  onClose: () => void;
}

const SERVICE_CONFIG = {
  delivery: {
    title: "Богатый Курьер",
    botName: "BogatiyKurierBot",
    text: [
      "Ежедневный вывод средств на любую карту",
      "Ежемесячные и ежедневные турниры",
      "Прозрачная реферальная программа от БК",
    ],
  },
  taxi: {
    title: "Богатый Таксист",
    botName: "BogatiyTaxistBot",
    text: [
      "Ежедневный вывод средств на любую карту",
      "Персональные денежные цели от парка",
      "Прозрачная реферальная программа от БТ",
    ],
  },
  eats: {
    title: "Богатый Курьер Еда",
    botName: "BK_EdaBot",
    text: [
      "Ежедневный вывод средств на любую карту",
      "Самые высокие денежные цели",
      "Постоянный поток заказов без простоя",
      "Прозрачная реферальная программа от БК",
    ],
  },
};

export const QrSection = ({
  inConditions,
  variant,
  onClose,
}: QrSectionProps) => {
  const yandexId = useUserInfoStore((s) => s.yandexId);

  const config = SERVICE_CONFIG[variant];
  const utm = inConditions ? "RTQR" : "RMQR";
  const link = `https://t.me/${config.botName}?start=yr_${yandexId}_${utm}`;

  const screenBlockRef = useRef<HTMLDivElement>(null);
  const [shareLoading, setShareLoading] = useState<boolean>(false);

  const handleShare = async () => {
    if (!screenBlockRef.current) return;
    setShareLoading(true);

    const dataUrl = await htmlToImage.toPng(screenBlockRef.current);

    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], "qr-code.png", { type: "image/png" });

    if (navigator.share) {
      try {
        await navigator.share({
          files: [file],
          title: "QR Code",
        });
      } catch (e) {
        console.error("Share failed:", e);
      } finally {
        setShareLoading(false);
      }
    } else {
      console.log("Функция шаринга не поддерживается на этом устройстве");
      setShareLoading(false);
    }
    setShareLoading(false);
  };

  return (
    <FlexColumn gap={0} className={cls.qrSection}>
      <CloseBtn onclose={onClose} trigger="ref.qr.close" />

      <div ref={screenBlockRef} className={cls.qrCard}>
        <FlexColumn gap={4}>
          <Typography className={cls.heading}>Твой реферальный QR</Typography>
          <Typography className={cls.qrCardLabel}>{config.title}</Typography>
        </FlexColumn>

        <div className={cls.qrBlock}>
          <QRCodeSVG value={link} size={200} className={cls.qr} />
        </div>

        <FlexColumn gap={16}>
          <Typography className={cls.qrTitle}>
            После регистрации получите:
          </Typography>

          <FlexColumn gap={12}>
            {config.text.map((elem) => (
              <FlexRow alignItems="flex-start" gap={8} key={elem}>
                <div className={cls.checkMark}>
                  <CheckMark />
                </div>
                <Typography className={cls.infoItem}>{elem}</Typography>
              </FlexRow>
            ))}
          </FlexColumn>
        </FlexColumn>
      </div>

      <Button
        trigger="ref.qr.share"
        icon={<ShareIcon />}
        label="Поделиться"
        loading={shareLoading}
        disabled={shareLoading}
        className={cls.shareBtn}
        onClick={handleShare}
      />
    </FlexColumn>
  );
};
